import { useEffect, useState } from "preact/hooks";

import { type FunctionComponent } from "preact";
import { JS_API_OBJECT_NAME } from "core/config";
import { FALSE, NULL, TRUE } from "constants/primitives";
import { _WINDOW_ } from "constants/dom";
import dispatchAppInitializedEvent from "helpers/dispatchAppInitializedEvent";
import fetchSiteCookies from "helpers/fetchSiteCookies";
import integrationSettings from "core/integrationSettings";
import useAppState from "hooks/useAppState";
import useTranslation from "hooks/useTranslation";
import useUserConsent from "hooks/useUserConsent";
import useTCFUserConsent from "hooks/useTCFUserConsent";
import {
  createCookieFirstPublicApiTCFInstance,
  type CookieFirstPublicApiTCF,
} from "./CookieFirstPublicApiTCF";
import dispatchTCFInitializedEvent from "helpers/tcf/dispatchTCFInitializedEvent";
import dispatchApiSyncEvent from "helpers/dispatchApiSyncEvent";
import dispatchConsentEvent from "helpers/dispatchConsentEvent";
import dispatchTCFConsentUpdated from "helpers/tcf/tcfHelpers/dispatchTCFConsentUpdated";

const PublicApiTCF: FunctionComponent = () => {
  const { stealthMode } = integrationSettings;
  const {
    changePanelTab,
    widgetConfig,
    gvl,
    gatp,
    isTCFPublicEndpointInit,
    shouldTriggerConsentEvent,
    shouldTriggerTCFConsentEvent,
    setShouldTriggerConsentEvent,
    setShouldTriggerTCFConsentEvent,
  } = useAppState();
  const consent = useUserConsent();
  const [isInitialized, setIsInitialized] = useState(FALSE);
  const { userLang, setUserLang } = useTranslation();
  const TCFConsent = useTCFUserConsent();

  useEffect(() => {
    _WINDOW_[JS_API_OBJECT_NAME] = createCookieFirstPublicApiTCFInstance({
      setPanelTab: changePanelTab,
      consent,
      TCFConsent,
      widgetConfig,
      stealthMode,
      fetchLatestScan: () => fetchSiteCookies(userLang, widgetConfig.version),
      setUserLang,
      gvl,
      gatp,
    });

    if (!isInitialized && isTCFPublicEndpointInit) {
      setIsInitialized(TRUE);
      dispatchAppInitializedEvent();
      dispatchTCFInitializedEvent();
    }

    if (import.meta.env.VITE_APP_WHITELABEL_MODE !== "true") {
      _WINDOW_.cookiefirst_show_settings = _WINDOW_[JS_API_OBJECT_NAME]
        .openPanel as CookieFirstPublicApiTCF["openPanel"];
    }
  }, [
    isInitialized,
    changePanelTab,
    consent,
    TCFConsent,
    userLang,
    stealthMode,
    widgetConfig,
    setUserLang,
    gvl,
    gatp,
    isTCFPublicEndpointInit,
    shouldTriggerTCFConsentEvent,
    shouldTriggerTCFConsentEvent,
  ]);

  useEffect(() => {
    if (shouldTriggerConsentEvent) {
      dispatchConsentEvent(
        consent.userConsent,
        consent.hasConsented,
        widgetConfig
      );
      setShouldTriggerConsentEvent(FALSE);
    }
  }, [
    shouldTriggerConsentEvent,
    setShouldTriggerConsentEvent,
    consent.userConsent,
    consent.hasConsented,
    widgetConfig,
  ]);

  useEffect(() => {
    if (shouldTriggerTCFConsentEvent) {
      dispatchTCFConsentUpdated();
      setShouldTriggerTCFConsentEvent(FALSE);
    }
  }, [shouldTriggerTCFConsentEvent, setShouldTriggerTCFConsentEvent]);

  // Dispatch event to sync api when some of the static values has changed
  useEffect(() => {
    dispatchApiSyncEvent();
  }, [gvl, gatp, TCFConsent.eventStatus]);

  return NULL;
};

export default PublicApiTCF;
